/**
 * Branches API + TanStack Query hooks (Phase 4 — Calendar).
 *
 * Endpoints:
 *   GET   /v1/calendar/branches
 *   POST  /v1/calendar/branches
 *   PATCH /v1/calendar/branches/{id}
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, tenantHeaderOverride } from '@core/api/client';
import type { ApiCallOptions } from '@core/api/client';
import { useTenant } from '@core/tenant/TenantContext';
import { useTenantSafeQuery } from '@core/tenant/useTenantSafeQuery';
import type { components } from '@api/index';

export type BranchResponse = components['schemas']['BranchResponse'];
export type BranchCreate = components['schemas']['BranchCreate'];
export type BranchUpdate = components['schemas']['BranchUpdate'];
export type PaginatedBranches = components['schemas']['PaginatedResult_BranchResponse_'];

export const branchKeys = {
  all: ['calendar', 'branches'] as const,
  // Tenant id folded in — an owner switching schools (11E.5) must not keep
  // showing the previous school's cached branches (queryClient.clear()
  // alone does not reliably refetch an already-mounted useQuery).
  list: (tid: string, status?: string) =>
    ['calendar', 'branches', 'list', tid, { status }] as const,
};

export async function listBranches(
  status?: string,
  tenantHeaders?: Pick<ApiCallOptions, 'headers'>,
): Promise<PaginatedBranches> {
  return api.get<PaginatedBranches>('/v1/calendar/branches', {
    query: { status: status || undefined },
    ...tenantHeaders,
  });
}

export async function createBranch(
  body: BranchCreate,
  tenantHeaders?: Pick<ApiCallOptions, 'headers'>,
): Promise<BranchResponse> {
  return api.post<BranchResponse>('/v1/calendar/branches', body, tenantHeaders);
}

export async function updateBranch(
  id: string,
  body: BranchUpdate,
  tenantHeaders?: Pick<ApiCallOptions, 'headers'>,
): Promise<BranchResponse> {
  return api.patch<BranchResponse>(`/v1/calendar/branches/${id}`, body, tenantHeaders);
}

export function useBranches(status?: string) {
  const { selectedTenantId, isAllMode } = useTenant();
  return useTenantSafeQuery({
    queryKey: branchKeys.list(selectedTenantId, status),
    queryFn: () => listBranches(status, tenantHeaderOverride(selectedTenantId, isAllMode)),
    staleTime: 60_000,
  });
}

export function useCreateBranch() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: (body: BranchCreate) =>
      createBranch(body, tenantHeaderOverride(selectedTenantId, isAllMode)),
    onSuccess: () => qc.invalidateQueries({ queryKey: branchKeys.all }),
  });
}

export function useUpdateBranch() {
  const qc = useQueryClient();
  const { selectedTenantId, isAllMode } = useTenant();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: BranchUpdate }) =>
      updateBranch(id, body, tenantHeaderOverride(selectedTenantId, isAllMode)),
    onSuccess: () => qc.invalidateQueries({ queryKey: branchKeys.all }),
  });
}

